// src/pages/admin/AdminLayout.jsx
import React, { useState } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import {
  FiGrid, FiPackage, FiTag, FiShoppingBag, FiUsers, FiFileText,
  FiImage, FiInfo, FiMail, FiLogOut, FiMenu, FiChevronRight, FiPrinter, FiStar,  FiHelpCircle, FiShare2, FiTruck,
  FiBriefcase, FiClipboard,
} from "react-icons/fi";
import { useAuth } from "../../context/AuthContext";
import toast from "react-hot-toast";
import logo from "../../assests/logo.png";

const NAV = [
  { to: "/admin",            icon: FiGrid,       label: "Dashboard"     },
  { to: "/admin/orders",     icon: FiShoppingBag,label: "Orders"        },
  { to: "/admin/order-unit", icon: FiTruck,      label: "Order Unit"    },

  { to: "/admin/products",   icon: FiPackage,    label: "Products"      },
  { to: "/admin/categories", icon: FiTag,        label: "Categories"    },
  { to: "/admin/banners",    icon: FiImage,      label: "Banners"       },
  { to: "/admin/blogs",      icon: FiFileText,   label: "Blogs"         },
  { to: "/admin/testimonials", icon: FiStar,     label: "Testimonials"  }, 
  { to: "/admin/faqs", icon: FiHelpCircle, label: "FAQ's" },
  { to: "/admin/social-links", icon: FiShare2, label: "Follow Us" },
  { to: "/admin/careers", icon: FiBriefcase, label: "Careers" },
  { to: "/admin/career-applications", icon: FiClipboard, label: "Career Applications" },
  { to: "/admin/about",      icon: FiInfo,       label: "About Us"      },
  { to: "/admin/users",      icon: FiUsers,      label: "Users"         },
    { to: "/admin/bills",      icon: FiPrinter,    label: "Physical Bills" }, 
];

export default function AdminLayout() {
  const { logout, user }  = useAuth();
  const location          = useLocation();
  const navigate          = useNavigate();
  const [sideOpen, setSideOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    toast.success("Logged out");
    navigate("/");
  };

  const isActive = (to) =>
    to === "/admin" ? location.pathname === "/admin" : location.pathname.startsWith(to);

  const Sidebar = ({ mobile = false }) => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="px-5 py-5 border-b border-white/10">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-10 h-10 bg-white rounded-lg shadow-md flex items-center justify-center p-1">
            <img src={logo} alt="Logo" className="w-full h-full object-contain" />
          </div>
          <div>
            <p className="font-display font-bold text-white text-lg leading-none">Tripura Agro</p>
            <p className="text-white/50 text-xs">Admin Panel</p>
          </div>
        </Link>
      </div>

      {/* Nav links */}
      <nav className="flex-1 py-4 px-3 space-y-0.5 overflow-y-auto">
        {NAV.map(({ to, icon: Icon, label }) => {
          const active = isActive(to);
          return (
            <Link
              key={to} to={to}
              onClick={() => setSideOpen(false)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                active
                  ? "bg-white/20 text-white"
                  : "text-white/70 hover:bg-white/10 hover:text-white"
              }`}
            >
              <Icon className="w-4 h-4 flex-shrink-0" />
              <span className="flex-1">{label}</span>
              {active && <FiChevronRight className="w-3.5 h-3.5 opacity-70" />}
            </Link>
          );
        })}
      </nav>

      {/* User + logout */}
      <div className="px-3 py-4 border-t border-white/10">
        <div className="flex items-center gap-3 px-3 py-2 mb-2">
          <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-sm font-bold text-white flex-shrink-0">
            {(user?.displayName || user?.email || "A")[0].toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="text-white text-sm font-medium truncate">{user?.displayName || "Admin"}</p>
            <p className="text-white/50 text-xs truncate">{user?.email}</p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-white/70 hover:bg-white/10 hover:text-white transition-all w-full"
        >
          <FiLogOut className="w-4 h-4" /> Logout
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-gray-100 overflow-hidden">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col w-60 bg-leaf-600 flex-shrink-0">
        <Sidebar />
      </aside>

      {/* Mobile sidebar overlay */}
      {sideOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSideOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-64 bg-leaf-600 flex flex-col z-10">
            <Sidebar mobile />
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top bar */}
        <header className="bg-white shadow-sm flex items-center justify-between px-4 sm:px-6 h-14 flex-shrink-0">
          <button onClick={() => setSideOpen(true)} className="lg:hidden p-2 rounded-lg hover:bg-gray-100">
            <FiMenu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3 ml-auto">
            <Link to="/" target="_blank" className="text-sm text-gray-500 hover:text-primary-600 transition-colors">
              View Store ↗
            </Link>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-4 sm:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}